import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import useQuery from '../../../custom-hooks/useQuery';
import { SubmitApprovalViaMail } from '../../../services/attendance-change';

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

/*
 * Single-flight protection for approval links.
 * React 18 StrictMode may run the effect more than once during development;
 * the same approval-link request reuses the same Promise, so only one API
 * call is sent while every mounted instance can still receive the result.
 */
const ftoApprovalRequestCache = new Map();

const executeApprovalOnce = (key, requestFactory) => {
  if (!ftoApprovalRequestCache.has(key)) {
    ftoApprovalRequestCache.set(
      key,
      Promise.resolve().then(requestFactory)
    );
  }

  return ftoApprovalRequestCache.get(key);
};

export const ThankyouPage = () => {
  const theme = useTheme();

  const { ftoId } = useParams();
  const query = useQuery();

  const viewValue = query.get('case') || '';

  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [ftoTakenName, setFtoTakenName] = useState('');
  const [requestTypeId, setRequestTypeId] = useState(null);
  const [approverName, setApproverName] = useState('');
  const [processedAs, setProcessedAs] = useState('');

  const approvalStartedRef = useRef(false);

  /*
   * Correct email format:
   * ?case=ApprovalforFTO&Approve=true&userId=1580
   *
   * Keep compatibility with the legacy format:
   * ?case=ApprovalforFTO?Approve=true?userId=1580
   */
  let userId = query.get('userId') || '';
  let isApprove = query.get('Approve') || '';
  let approvalCase = viewValue || '';

  if (viewValue && (!userId || !isApprove)) {
    const parts = viewValue.split('?');

    approvalCase = parts[0] || '';

    if (!userId) {
      const userPart = parts.find((part) =>
        part.toLowerCase().startsWith('userid=')
      );
      userId = userPart ? userPart.split('=')[1] : '';
    }

    if (!isApprove) {
      const approvePart = parts.find((part) =>
        part.toLowerCase().startsWith('approve=')
      );
      isApprove = approvePart ? approvePart.split('=')[1] : '';
    }
  }

  const normalizedAction = String(isApprove || '').toLowerCase();

  const isApproval = normalizedAction === 'true';
  const isDenial = normalizedAction === 'false';

  const isValidRequest =
    Boolean(ftoId) &&
    Boolean(userId) &&
    (isApproval || isDenial);

  /*
   * Auto-approve on a real visible browser visit.
   *
   * Why the visibility gate:
   * mail clients/security scanners can open approval URLs automatically.
   * Those background requests must not execute the approval action.
   * When the deputy actually opens the link in a visible browser tab,
   * the approval is submitted automatically without requiring another click.
   */
  const submitApproval = async () => {
    if (
      !isValidRequest ||
      approvalStartedRef.current ||
      document.visibilityState !== 'visible'
    ) {
      return;
    }

    approvalStartedRef.current = true;
    setStatus('loading');
    setMessage('');

    const requestKey = [
      'fto',
      ftoId,
      userId,
      normalizedAction,
      approvalCase || 'approval',
    ].join('_');

    try {
      const data = {
        ftoId: Number(ftoId),
        isApprove: isApprove,
        note: isApproval ? 'Approved' : 'Denied',
        userId: userId,
      };

      const response = await executeApprovalOnce(
        requestKey,
        () => SubmitApprovalViaMail(data)
      );

      if (response?.status !== 200) {
        throw new Error('FTO approval endpoint returned a non-success status.');
      }

      const result = response?.data;

      const returnedFtoTypeId = Number(result?.ftoTypeId);
      if (returnedFtoTypeId === 1 || returnedFtoTypeId === 2) {
        setRequestTypeId(returnedFtoTypeId);
      }

      setFtoTakenName(result?.createdBy || '-');
      setApproverName(result?.approvalPersonName || '');

      const mailRowType =
        String(result?.mailRowType || '').toLowerCase();

      /*
       * If another request already completed the same approval, the
       * database has still reached the requested final state. Show the
       * final result instead of confusing the deputy with "Already Processed".
       */
      if (mailRowType === 'alreadyprocessed') {
        const actionResult =
          String(result?.actionResult || '').toUpperCase();

        if (actionResult === 'ALREADY_DENIED') {
          setProcessedAs('denied');
          setStatus('already');
          setMessage(
            `This ${returnedFtoTypeId === 2 ? 'FTI' : 'FTO'} has already been denied. No further action is required.`
          );
        } else {
          setProcessedAs('approved');
          setStatus('already');
          setMessage(
            `This ${returnedFtoTypeId === 2 ? 'FTI' : 'FTO'} has already been approved. No further action is required.`
          );
        }

        return;
      }

      if (isApproval) {
        setProcessedAs('approved');
        setStatus('approved');
        setMessage(
          `Your ${returnedFtoTypeId === 2 ? 'FTI' : 'FTO'} approval has been recorded successfully.`
        );
      } else {
        setProcessedAs('denied');
        setStatus('denied');
        setMessage(
          `Your ${returnedFtoTypeId === 2 ? 'FTI' : 'FTO'} denial has been recorded successfully.`
        );
      }
    } catch (error) {
      console.error('Approval request failed:', error);
      setStatus('error');
      setMessage(
        `The ${requestCode || 'FTO'} action could not be completed. Please try the email link again.`
      );
      approvalStartedRef.current = false;
    }
  };

  useEffect(() => {
    if (!isValidRequest) {
      setStatus('invalid');
      setMessage(
        'This approval link is incomplete or invalid.'
      );
      return undefined;
    }

    // Normal user click from email opens a visible page.
    submitApproval();

    // Also handle clients that initially open the page hidden.
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        submitApproval();
      }
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibilityOrFocus
    );
    window.addEventListener('focus', handleVisibilityOrFocus);

    return () => {
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityOrFocus
      );
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [
    ftoId,
    userId,
    normalizedAction,
    approvalCase,
    isApproval,
    isValidRequest,
  ]);

  const requestCode =
    requestTypeId === 2
      ? 'FTI'
      : requestTypeId === 1
        ? 'FTO'
        : '';

  const requestName =
    requestTypeId === 2
      ? 'Flexible Leave In'
      : requestTypeId === 1
        ? 'Flexible Leave Off'
        : 'Approval Request';

  const statusConfig = useMemo(() => {
    const code = requestCode || 'Approval';

    switch (status) {
      case 'approved':
        return {
          title: `${code} Approved`,
          subtitle:
            'The approval workflow was completed successfully.',
          icon: '✓',
          palette: theme.palette.success,
          statusLabel: 'Approved',
        };

      case 'denied':
        return {
          title: `${code} Denied`,
          subtitle:
            'The approval workflow was completed with a denial.',
          icon: '×',
          palette: theme.palette.error,
          statusLabel: 'Denied',
        };

      case 'already':
        return {
          title: `${code} Already Processed`,
          subtitle:
            'This approval link has already been processed and no further action is required.',
          icon: '✓',
          palette: theme.palette.warning,
          statusLabel: 'Already Processed',
        };

      case 'invalid':
        return {
          title: 'Invalid Approval Link',
          subtitle:
            'The required approval information could not be verified.',
          icon: '!',
          palette: theme.palette.warning,
          statusLabel: 'Invalid',
        };

      case 'error':
        return {
          title: `Unable to Process ${code}`,
          subtitle:
            'The request could not be verified or processed.',
          icon: '!',
          palette: theme.palette.error,
          statusLabel: 'Not Processed',
        };

      default:
        return {
          title: requestTypeId
            ? isApproval
              ? `Processing ${code} Approval`
              : isDenial
                ? `Processing ${code} Denial`
                : `Processing ${code} Request`
            : 'Processing Approval Request',
          subtitle:
            'Please wait while your request is being verified and processed.',
          icon: null,
          palette: theme.palette.primary,
          statusLabel: 'Processing',
        };
    }
  }, [
    status,
    isApproval,
    isDenial,
    requestTypeId,
    requestCode,
    requestName,
    theme.palette,
  ]);

  const isLoading = status === 'loading';

  const actionLabel = isApproval
    ? 'Approve'
    : isDenial
      ? 'Deny'
      : 'Unknown';

  const actionPalette =
    status === 'denied'
      ? theme.palette.error
      : theme.palette.success;

  const alertSeverity =
    status === 'approved'
      ? 'success'
      : status === 'denied'
        ? 'error'
        : status === 'already'
          ? 'warning'
          : status === 'invalid'
            ? 'warning'
            : 'error';

  const bottomText = isLoading
    ? `Your ${requestCode || 'approval'} request is being processed.`
    : status === 'approved' ||
      status === 'denied' ||
      status === 'already'
      ? 'No further action is required. You may safely close this page.'
      : 'Please verify that you opened the latest approval link from your email.';

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: {
          xs: 'calc(100vh - 120px)',
          md: 'calc(100vh - 90px)',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },
        py: {
          xs: 3,
          md: 5,
        },
        bgcolor: 'background.default',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          width: 420,
          height: 420,
          borderRadius: '50%',
          bgcolor: alpha(
            theme.palette.primary.main,
            0.035
          ),
          top: -190,
          right: -130,
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          width: 320,
          height: 320,
          borderRadius: '50%',
          bgcolor: alpha(
            theme.palette.primary.main,
            0.025
          ),
          bottom: -170,
          left: -100,
          pointerEvents: 'none',
        }}
      />

      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 680,
          position: 'relative',
          zIndex: 1,
          overflow: 'hidden',
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 18px 55px ${alpha(
            theme.palette.common.black,
            theme.palette.mode === 'dark'
              ? 0.28
              : 0.08
          )}`,
        }}
      >
        {/* Top status line */}
        <Box
          sx={{
            height: 4,
            bgcolor: statusConfig.palette.main,
          }}
        />

        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            px: {
              xs: 2.5,
              sm: 3.5,
            },
            py: 2.5,
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              flexShrink: 0,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(
                theme.palette.primary.main,
                0.1
              ),
              border: `1px solid ${alpha(
                theme.palette.primary.main,
                0.2
              )}`,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 15,
                color: 'primary.main',
                letterSpacing: 0.5,
              }}
            >
              {requestCode || 'APP'}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: {
                  xs: 14,
                  sm: 16,
                },
                color: 'text.primary',
              }}
            >
              {requestName}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.25,
                color: 'text.secondary',
                fontSize: 12.5,
              }}
            >
              Approval Management
            </Typography>
          </Box>
        </Stack>

        <Divider />

        {/* Main content */}
        <Box
          sx={{
            px: {
              xs: 2.5,
              sm: 4,
            },
            py: {
              xs: 4,
              sm: 5,
            },
            textAlign: 'center',
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              minHeight: 82,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(
                    theme.palette.primary.main,
                    0.06
                  ),
                }}
              >
                <CircularProgress
                  size={42}
                  thickness={4}
                  color="primary"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(
                    statusConfig.palette.main,
                    0.08
                  ),
                  border: `2px solid ${alpha(
                    statusConfig.palette.main,
                    0.28
                  )}`,
                  color: statusConfig.palette.main,
                  fontWeight: 800,
                  fontSize: 40,
                  lineHeight: 1,
                }}
              >
                {statusConfig.icon}
              </Box>
            )}
          </Box>

          {/* Title */}
          <Typography
            component="h1"
            sx={{
              color: isLoading
                ? 'text.primary'
                : statusConfig.palette.main,
              fontWeight: 700,
              fontSize: {
                xs: 23,
                sm: 28,
              },
              lineHeight: 1.25,
            }}
          >
            {statusConfig.title}
          </Typography>

          {/* Subtitle */}
          <Typography
            sx={{
              mt: 1,
              mx: 'auto',
              maxWidth: 520,
              color: 'text.secondary',
              fontSize: {
                xs: 13.5,
                sm: 14.5,
              },
              lineHeight: 1.7,
            }}
          >
            {statusConfig.subtitle}
          </Typography>

          {/* Message */}
          {!isLoading && message && (
            <Alert
              severity={alertSeverity}
              icon={false}
              sx={{
                mt: 3,
                borderRadius: 2,
                textAlign: 'left',
                border: `1px solid ${alpha(
                  statusConfig.palette.main,
                  0.2
                )}`,
                bgcolor: alpha(
                  statusConfig.palette.main,
                  0.05
                ),
                color: 'text.primary',
              }}
            >
              {message}
            </Alert>
          )}

          {/* Details */}
          <Box
            sx={{
              mt: 3.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              bgcolor: alpha(
                theme.palette.background.default,
                theme.palette.mode === 'dark'
                  ? 0.25
                  : 0.45
              ),
              textAlign: 'left',
            }}
          >
            <DetailRow
              theme={theme}
              label={`${requestCode || 'Request'} Taken`}
              value={ftoTakenName || '-'}
            />

            <Divider />

            <DetailRow
              theme={theme}
              label="Requested Action"
              customValue={
                <Chip
                  size="small"
                  label={actionLabel}
                  sx={{
                    minWidth: 78,
                    fontWeight: 700,
                    bgcolor: alpha(
                      actionPalette.main,
                      0.08
                    ),
                    color: actionPalette.main,
                    border: `1px solid ${alpha(
                      actionPalette.main,
                      0.15
                    )}`,
                  }}
                />
              }
            />

            <Divider />

            <DetailRow
              theme={theme}
              label="Status"
              customValue={
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  {isLoading && (
                    <CircularProgress
                      size={15}
                      thickness={5}
                    />
                  )}

                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: isLoading
                        ? 'text.primary'
                        : statusConfig.palette.main,
                    }}
                  >
                    {statusConfig.statusLabel}
                  </Typography>
                </Stack>
              }
            />

            {!isLoading && approverName && processedAs && (
              <>
                <Divider />
                <DetailRow
                  theme={theme}
                  label={processedAs === 'denied' ? 'Denied by' : 'Approved by'}
                  value={approverName}
                />
              </>
            )}
          </Box>

          {/* Bottom information */}
          <Box
            sx={{
              mt: 3,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: alpha(
                theme.palette.primary.main,
                0.035
              ),
            }}
          >
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 12.5,
                lineHeight: 1.6,
              }}
            >
              {bottomText}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 1.75,
            bgcolor: alpha(
              theme.palette.background.default,
              0.5
            ),
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: 11.5,
              color: 'text.secondary',
            }}
          >
            SSWhite Unified ERP • {requestCode || 'Approval'} Approval
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const DetailRow = ({
  theme,
  label,
  value,
  customValue,
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{
        px: {
          xs: 2,
          sm: 2.5,
        },
        py: 1.7,
        '&:hover': {
          bgcolor: alpha(
            theme.palette.primary.main,
            0.025
          ),
        },
      }}
    >
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 13.5,
        }}
      >
        {label}
      </Typography>

      {customValue || (
        <Typography
          sx={{
            color: 'text.primary',
            fontSize: 13.5,
            fontWeight: 700,
          }}
        >
          {value}
        </Typography>
      )}
    </Stack>
  );
};
